/** Generic helpers to rewrite relative URLs inside HTML fragments to absolute URLs. */

/** Return true if url is already absolute (has scheme or protocol-relative). */
const isAbsolute = (url: string) => /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url);

/** Return true if url is an embedded/inline reference that should not be touched. */
const isInlineLike = (url: string) => /^(?:data:|blob:|file:)/i.test(url);

/** Absolutize a possibly-relative url against an origin (e.g. https://example.com). */
export const absolutizeAgainst = (url: string, origin: string): string => {
  if (!url) return url;
  if (isAbsolute(url) || isInlineLike(url)) return url;
  if (url.startsWith("/")) return `${origin}${url}`;
  return `${origin}/${url}`;
};

/**
 * Rewrite <img> (src, srcset) and <source> (src, srcset) attributes to absolute URLs.
 * You can pass additional tag/attribute pairs via options to support more tags.
 */
export function rewriteRelativeMediaUrls(
  html: string,
  origin: string,
  options?: {
    /** Extra tag to attributes map, e.g. { a: ["href"], link: ["href"], script: ["src"] } */
    extraTagAttrs?: Record<string, string[]>;
  }
): string {
  const container = document.createElement("div");
  container.innerHTML = html;

  const tagAttrs: Record<string, string[]> = {
    img: ["src", "srcset"],
    source: ["src", "srcset"],
    ...(options?.extraTagAttrs ?? {}),
  };

  const handleSrcset = (el: Element, attr: string) => {
    const srcset = el.getAttribute(attr);
    if (!srcset) return;
    const parts = srcset
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .map((entry) => {
        const [u, d] = entry.split(/\s+/, 2);
        const abs = absolutizeAgainst(u, origin);
        return d ? `${abs} ${d}` : abs;
      });
    el.setAttribute(attr, parts.join(", "));
  };

  for (const [tag, attrs] of Object.entries(tagAttrs)) {
    for (const attr of attrs) {
      const nodes = Array.from(container.querySelectorAll(`${tag}[${attr}]`));
      nodes.forEach((node) => {
        if (attr.toLowerCase() === "srcset") {
          handleSrcset(node, attr);
        } else {
          const val = node.getAttribute(attr) || "";
          const abs = absolutizeAgainst(val.trim(), origin);
          if (abs !== val) node.setAttribute(attr, abs);
        }
      });
    }
  }

  return container.innerHTML;
}
